// HLS playlists (RFC 8216, with the low-latency additions of its second edition) as far as the
// player reads them (docs/specs/video-hls.md VH-3, VH-4): a master playlist's variants, and a
// media playlist's initialisation section, segments and parts.

export interface Variant {
    uri: string;
    codecs: string | undefined;
}

/** One piece the player can fetch: a whole segment, or a part of one. */
export interface MediaPiece {
    uri: string;
    /** Seconds. */
    duration: number;
    /** The segment's media sequence number. */
    msn: number;
    /** The part's index in its segment; undefined for a whole segment. */
    part: number | undefined;
    /**
     * It holds a key picture (`INDEPENDENT=YES`; a whole segment where the playlist says all are):
     * playing can begin here. Not always its first picture: Home Assistant's 4.5 had it as its 11th,
     * and the decoder drops those before it.
     */
    independent: boolean;
}

export interface MediaPlaylist {
    /** `#EXT-X-MAP`: the fMP4 initialisation section. */
    map: string | undefined;
    targetDuration: number;
    partTarget: number | undefined;
    /** `PART-HOLD-BACK`, seconds behind the end that playing should keep. */
    partHoldBack: number | undefined;
    /** The server can hold a reload until a part is there (`_HLS_msn`, `_HLS_part`). */
    canBlockReload: boolean;
    /** Whole segments, in order. */
    segments: MediaPiece[];
    /** The parts, in order: those of finished segments and of the one being made. Empty without low latency. */
    parts: MediaPiece[];
    /** No more segments will come (`#EXT-X-ENDLIST`). */
    ended: boolean;
}

/** The attributes of a tag: `DURATION=0.920,URI="./segment/0.0.m4s",INDEPENDENT=YES`. */
export function attributesOf(list: string) : Map<string, string> {
    const attributes = new Map<string, string>();
    for (const match of list.matchAll(/([A-Z0-9-]+)=("[^"]*"|[^,]*)/g)) {
        const [, name, raw] = match;
        attributes.set(name!, raw!.startsWith('"') ? raw!.slice(1, -1) : raw!);
    }

    return attributes;
}

const lines = (text: string) : string[] => text.split(/\r?\n/).map((line) => line.trim()).filter((line) => line !== '');

export function isMasterPlaylist(text: string) : boolean {
    return /^#EXT-X-STREAM-INF:/m.test(text);
}

/** A master playlist's variants, in order. */
export function parseMasterPlaylist(text: string) : Variant[] {
    const variants: Variant[] = [];
    let pending: Map<string, string> | undefined;
    for (const line of lines(text)) {
        if (line.startsWith('#EXT-X-STREAM-INF:')) {
            pending = attributesOf(line.slice('#EXT-X-STREAM-INF:'.length));
        } else if (!line.startsWith('#') && pending) {
            variants.push({ uri: line, codecs: pending.get('CODECS') });
            pending = undefined;
        }
    }

    return variants;
}

export function parseMediaPlaylist(text: string) : MediaPlaylist {
    const playlist: MediaPlaylist = { map: undefined, targetDuration: 0, partTarget: undefined, partHoldBack: undefined, canBlockReload: false, segments: [], parts: [], ended: false };
    let msn = 0;
    let part = 0;
    let duration: number | undefined;
    let allIndependent = false;
    let firstPartIndependent = false;
    for (const line of lines(text)) {
        if (!line.startsWith('#')) {
            if (duration !== undefined) {
                // A segment is independent where the playlist says all are, or where its first part is.
                playlist.segments.push({ uri: line, duration, msn, part: undefined, independent: allIndependent || firstPartIndependent });
            }
            msn++;
            part = 0;
            duration = undefined;
            firstPartIndependent = false;
            continue;
        }
        const colon = line.indexOf(':');
        const tag = colon === -1 ? line : line.slice(0, colon);
        const value = colon === -1 ? '' : line.slice(colon + 1);
        switch (tag) {
            case '#EXT-X-MEDIA-SEQUENCE':
                msn = Number(value) || 0;
                break;
            case '#EXT-X-TARGETDURATION':
                playlist.targetDuration = Number(value) || 0;
                break;
            case '#EXT-X-INDEPENDENT-SEGMENTS':
                allIndependent = true;
                break;
            case '#EXT-X-MAP':
                playlist.map = attributesOf(value).get('URI');
                break;
            case '#EXT-X-PART-INF':
                playlist.partTarget = Number(attributesOf(value).get('PART-TARGET')) || undefined;
                break;
            case '#EXT-X-SERVER-CONTROL': {
                const control = attributesOf(value);
                playlist.canBlockReload = control.get('CAN-BLOCK-RELOAD') === 'YES';
                playlist.partHoldBack = Number(control.get('PART-HOLD-BACK')) || undefined;
                break;
            }
            case '#EXT-X-PART': {
                const attributes = attributesOf(value);
                const uri = attributes.get('URI');
                if (uri !== undefined) {
                    const independent = attributes.get('INDEPENDENT') === 'YES';
                    if (part === 0) {
                        firstPartIndependent = independent;
                    }
                    playlist.parts.push({ uri, duration: Number(attributes.get('DURATION')) || 0, msn, part, independent });
                    part++;
                }
                break;
            }
            case '#EXTINF':
                duration = Number.parseFloat(value) || 0;
                break;
            case '#EXT-X-ENDLIST':
                playlist.ended = true;
                break;
        }
    }

    return playlist;
}

/** `relative` read against the playlist it was in. */
export function resolveUri(relative: string, base: string) : string {
    return new URL(relative, base).href;
}
